import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useMergeRefs,
  useRole,
  useTransitionStatus,
  useTypeahead,
  type Placement,
} from '@floating-ui/react';
import { Check } from 'lucide-react';
import {
  cloneElement,
  isValidElement,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { cx, useControllableState } from '../utils.ts';
import { Kbd } from './Kbd.tsx';
import './overlay.css';

export type MenuItem =
  | {
      type?: 'item';
      key?: string;
      label: ReactNode;
      /** Plain text used for typeahead when `label` is not a string. */
      textValue?: string;
      icon?: ReactNode;
      shortcut?: string;
      hint?: ReactNode;
      danger?: boolean;
      disabled?: boolean;
      /** Show a check mark column (true/false) for toggle items. */
      checked?: boolean;
      onSelect?: () => void;
      /** Keep the menu open after selecting (toggles). */
      keepOpen?: boolean;
    }
  | { type: 'separator'; key?: string }
  | { type: 'label'; key?: string; label: ReactNode };

type ItemsProp = MenuItem[] | (() => MenuItem[]);

const isItem = (i: MenuItem): i is Extract<MenuItem, { label: ReactNode; type?: 'item' }> => i.type === undefined || i.type === 'item';

function useMenuCore(open: boolean, setOpen: (v: boolean) => void, placement: Placement, trigger: boolean) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const itemsRef = useRef<(HTMLElement | null)[]>([]);
  const labelsRef = useRef<(string | null)[]>([]);
  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({ mainAxis: 6, alignmentAxis: trigger ? 0 : 2 }),
      flip({ padding: 10 }),
      shift({ padding: 10 }),
      sizeMiddleware({
        padding: 10,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(160, availableHeight)}px`;
        },
      }),
    ],
  });
  const click = useClick(floating.context, { enabled: trigger });
  const dismiss = useDismiss(floating.context);
  const role = useRole(floating.context, { role: 'menu' });
  const nav = useListNavigation(floating.context, { listRef: itemsRef, activeIndex, onNavigate: setActiveIndex, loop: true });
  const typeahead = useTypeahead(floating.context, { listRef: labelsRef, activeIndex, onMatch: open ? setActiveIndex : undefined });
  const interactions = useInteractions([click, dismiss, role, nav, typeahead]);
  const transition = useTransitionStatus(floating.context, { duration: { open: 150, close: 100 } });
  return { floating, interactions, transition, activeIndex, itemsRef, labelsRef };
}

function MenuPanel({
  core,
  items,
  onClose,
  minWidth,
  className,
}: {
  core: ReturnType<typeof useMenuCore>;
  items: MenuItem[];
  onClose: () => void;
  minWidth?: number;
  className?: string;
}) {
  const { floating, interactions, transition, activeIndex, itemsRef, labelsRef } = core;
  itemsRef.current = [];
  labelsRef.current = [];
  const hasChecks = items.some((i) => isItem(i) && i.checked !== undefined);
  return (
    <FloatingPortal>
      <FloatingFocusManager context={floating.context} modal={false} initialFocus={0} order={['floating', 'content']} returnFocus>
        <div
          ref={floating.refs.setFloating}
          className={cx('g-popover g-menu', className)}
          data-status={transition.status}
          data-padding="none"
          style={{ ...floating.floatingStyles, minWidth }}
          {...interactions.getFloatingProps()}
        >
          {items.map((item, index) => {
            const key = item.key ?? index;
            if (item.type === 'separator') {
              itemsRef.current[index] = null;
              labelsRef.current[index] = null;
              return <div key={key} className="g-menu__sep" role="separator" />;
            }
            if (item.type === 'label') {
              itemsRef.current[index] = null;
              labelsRef.current[index] = null;
              return (
                <div key={key} className="g-menu__label">
                  {item.label}
                </div>
              );
            }
            labelsRef.current[index] = item.textValue ?? (typeof item.label === 'string' ? item.label : null);
            return (
              <button
                key={key}
                type="button"
                role={item.checked !== undefined ? 'menuitemcheckbox' : 'menuitem'}
                aria-checked={item.checked}
                className="g-menu__item"
                data-danger={item.danger || undefined}
                data-active={activeIndex === index || undefined}
                disabled={item.disabled}
                tabIndex={activeIndex === index ? 0 : -1}
                ref={(node) => {
                  itemsRef.current[index] = node;
                }}
                {...interactions.getItemProps({
                  onClick() {
                    item.onSelect?.();
                    if (!item.keepOpen) onClose();
                  },
                })}
              >
                {hasChecks && <span className="g-menu__check">{item.checked && <Check size={14} />}</span>}
                {item.icon && <span className="g-menu__icon">{item.icon}</span>}
                <span className="g-menu__text">{item.label}</span>
                {item.hint && <span className="g-menu__hint">{item.hint}</span>}
                {item.shortcut && <Kbd shortcut={item.shortcut} size="sm" className="g-menu__kbd" />}
              </button>
            );
          })}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}

export interface MenuProps {
  trigger: ReactElement;
  items: ItemsProp;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  minWidth?: number;
  className?: string;
}

/** Dropdown menu attached to a trigger element. */
export function Menu({ trigger, items, placement = 'bottom-start', open: openProp, onOpenChange, minWidth = 200, className }: MenuProps) {
  const [open, setOpen] = useControllableState(openProp, false, onOpenChange);
  const core = useMenuCore(open, setOpen, placement, true);
  const triggerProps = (isValidElement(trigger) ? trigger.props : {}) as Record<string, unknown> & { ref?: Ref<Element> };
  const ref = useMergeRefs([core.floating.refs.setReference, triggerProps.ref]);
  const resolved = useMemo(() => (open ? (typeof items === 'function' ? items() : items) : []), [open, items]);
  return (
    <>
      {cloneElement(trigger, core.interactions.getReferenceProps({ ...triggerProps, ref } as Record<string, unknown>))}
      {core.transition.isMounted && <MenuPanel core={core} items={resolved} onClose={() => setOpen(false)} minWidth={minWidth} className={className} />}
    </>
  );
}

export interface ContextMenuProps {
  items: ItemsProp;
  /** Element receiving `onContextMenu`; must accept props/ref. */
  children: ReactElement;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  minWidth?: number;
}

/** Right-click menu opening at the pointer. */
export function ContextMenu({ items, children, disabled, onOpenChange, minWidth = 200 }: ContextMenuProps) {
  const [open, setOpenState] = useState(false);
  const setOpen = (v: boolean) => {
    setOpenState(v);
    onOpenChange?.(v);
  };
  const core = useMenuCore(open, setOpen, 'right-start', false);
  const childProps = (isValidElement(children) ? children.props : {}) as Record<string, unknown> & {
    onContextMenu?: (e: ReactMouseEvent) => void;
  };
  const resolved = useMemo(() => (open ? (typeof items === 'function' ? items() : items) : []), [open, items]);
  if (disabled) return children;
  return (
    <>
      {cloneElement(children, {
        onContextMenu(e: ReactMouseEvent) {
          childProps.onContextMenu?.(e);
          if (e.defaultPrevented) return;
          e.preventDefault();
          const { clientX: x, clientY: y } = e;
          core.floating.refs.setPositionReference({
            getBoundingClientRect: () => ({ x, y, width: 0, height: 0, top: y, left: x, right: x, bottom: y }) as DOMRect,
          });
          setOpen(true);
        },
      } as Record<string, unknown>)}
      {core.transition.isMounted && <MenuPanel core={core} items={resolved} onClose={() => setOpen(false)} minWidth={minWidth} />}
    </>
  );
}
